/**
 * 显示比例（基础缩放）
 *
 * 背景：本项目没有自适应缩放，全站固定 px、按 1920 设计宽度布局
 * （详见 docs/topbar-visual-spec.md §10）。同样 14px 的 CSS 字号，
 * 在不同机器上的物理尺寸并不相同：Mac Retina（逻辑分辨率 1512×982、面板约 254 PPI）
 * 比 Windows 24" 1920×1080@100%（约 92 PPI）小约 28%，所以常有 Mac 用户反馈“字太小”。
 *
 * 做法：按屏幕密度给 body 加一个基础 zoom，一次性把字号和布局整体放大，
 * 不需要逐个组件改；同时允许用户在用户下拉里手动固定比例覆盖自动判断。
 *
 * 安全约束：放大不能让页面出现横向滚动条。
 * 注意不能只用 `zoom × 1200` 估算 —— 顶栏虽然写了 `min-width: 1200px`，
 * 但它的自然内容宽度（Logo + 导航项 + 新版入口 + 用户名）实测约 1226px，
 * 1200 只是理论下界。所以真正生效前会**实测一次 scrollWidth**，
 * 放不下就按 2% 退让，直到不溢出（最多退到 100%）。
 */

const STORAGE_KEY = 'dao_display_scale'

// 与 Home.vue 顶栏的 min-width 保持一致，改那边要同步改这里
const MIN_APP_WIDTH = 1200

// 自动缩放的上下限。上限 1.2 是所有现役 MacBook 都能容纳的量级；
// 实际值还会被下面的“实测退让”压到刚好放得进当前窗口。
const AUTO_MAX_ZOOM = 1.2

// 高密度屏门槛：Mac Retina = 2，Windows 150% 系统缩放 = 1.5
const HIGH_DENSITY_DPR = 1.5

// 退让步长与最多退让次数（1.2 -> 1.0 约需 10 次）
const FIT_STEP = 0.02
const FIT_MAX_TRIES = 12

export const SCALE_OPTIONS = [
  { label: '自动', value: 'auto' },
  { label: '100%', value: 1 },
  { label: '110%', value: 1.1 },
  { label: '120%', value: 1.2 }
]

const round2 = (n) => Math.round(n * 100) / 100

/** 当前可用于布局的宽度（排除纵向滚动条） */
const availableWidth = () => {
  if (typeof document !== 'undefined' && document.documentElement && document.documentElement.clientWidth) {
    return document.documentElement.clientWidth
  }
  if (typeof window !== 'undefined' && window.innerWidth) return window.innerWidth
  return 0
}

/** 按屏幕密度算出的自动比例（还没做“放不放得下”的实测退让） */
export const getAutoZoom = () => {
  if (typeof window === 'undefined') return 1
  const dpr = window.devicePixelRatio || 1
  // Windows 100% / 125% 这类低密度屏，CSS px 的物理尺寸本来就够大，不动
  if (dpr < HIGH_DENSITY_DPR) return 1
  const fit = availableWidth() / MIN_APP_WIDTH
  return Math.max(1, round2(Math.min(AUTO_MAX_ZOOM, fit)))
}

/** 读用户保存的比例偏好：'auto' 或 '1' / '1.1' / '1.2' 这样的字符串 */
export const getScalePref = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) || 'auto'
  } catch (e) {
    return 'auto'
  }
}

/** 页面当前是否出现了横向滚动条 */
export const hasHorizontalOverflow = () => {
  if (typeof document === 'undefined' || !document.documentElement) return false
  const de = document.documentElement
  return de.scrollWidth > de.clientWidth + 1
}

/**
 * 把比例写到 body 上，并实测确认没有横向溢出；放不下就逐档退让。
 * 读 scrollWidth 会强制一次同步布局，所以这里拿到的是加上 zoom 之后的真实结果，
 * 整个循环在同一帧内完成，不会闪烁。
 */
const applyFittingZoom = (target) => {
  if (typeof document === 'undefined' || !document.body) return target
  let zoom = target
  for (let i = 0; i < FIT_MAX_TRIES && zoom > 1; i++) {
    document.body.style.zoom = String(zoom)
    if (!hasHorizontalOverflow()) return zoom
    zoom = Math.max(1, round2(zoom - FIT_STEP))
  }
  document.body.style.zoom = ''
  return 1
}

/** 生效并返回实际比例。自动值会被实测退让；用户手动的值按用户的选择来。 */
export const applyZoom = () => {
  if (typeof document === 'undefined' || !document.body) return 1
  const pref = getScalePref()
  if (pref === 'auto') return applyFittingZoom(getAutoZoom())
  const manual = Number(pref)
  if (!(manual > 0)) return applyFittingZoom(getAutoZoom())
  document.body.style.zoom = manual === 1 ? '' : String(manual)
  return manual
}

/** 用户选择：'auto' 表示清除偏好、回到自动判断 */
export const setScalePref = (value) => {
  try {
    if (value === 'auto') localStorage.removeItem(STORAGE_KEY)
    else localStorage.setItem(STORAGE_KEY, String(value))
  } catch (e) {
    // 隐私模式下 localStorage 可能不可写，忽略即可，本次会话仍然生效
  }
  return applyZoom()
}

/** 把比例数字转成界面文案 */
export const describeZoom = (zoom) => Math.round((zoom || 1) * 100) + '%'
